import React, { useEffect, useState, useCallback } from "react";
import { NavLink, useNavigate, useLocation, Outlet } from "react-router-dom";
import {
  LayoutDashboard, Users, Building2, UserCog, LogOut, ShieldCheck, Globe, Bell, CalendarDays, MapPin, Landmark, Crown, History, Menu, X, Briefcase, Sparkles, ChevronsLeftRight, ArrowLeftRight, TrendingUp, Palmtree, FolderOpen, ClipboardCheck, BarChart3, Settings, ArrowRight, LayoutGrid,
} from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import NotifSummaryPanel from "@/components/NotifSummaryPanel";
import Brand from "@/components/Brand";
import { todayHijri, formatHijri } from "@/lib/hijri";
import "@/App.css";

export default function Layout() {
  const navigate = useNavigate();
  const { t, lang, setLang } = useT();
  const { user, logout } = useAuth();
  const [notifSummary, setNotifSummary] = useState({ total: 0 });
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const closeDrawer = () => setDrawerOpen(false);

  const loadNotifSummary = useCallback(async () => {
    try {
      const res = await api.get("/notifications/summary");
      setNotifSummary(res.data || { total: 0 });
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    loadNotifSummary();
    const interval = setInterval(loadNotifSummary, 60000);
    return () => clearInterval(interval);
  }, [loadNotifSummary]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  // Auto-logout after 30 minutes of inactivity
  useEffect(() => {
    const IDLE_MS = 30 * 60 * 1000;
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        handleLogout();
      }, IDLE_MS);
    };
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleLang = () => setLang(lang === "ar" ? "en" : "ar");
  const closeAlerts = () => { setAlertsOpen(false); loadNotifSummary(); };

  const location = useLocation();
  const _p = location.pathname;
  const headerMeta = {
    title: t("app_title"),
    subtitle:
      _p.startsWith("/employee-management") ? t("em_module_name")
      : _p.startsWith("/licenses") ? t("chooser_module_licenses_title")
      : _p.startsWith("/dayoff") ? "Day Off MenaMe"
      : _p.startsWith("/alerts") ? t("notif_center_title")
      : t("app_subtitle"),
  };

  const hijri = todayHijri();
  const todayHijriLabel = formatHijri(hijri);
  const todayG = new Date().toLocaleDateString(lang === "ar" ? "ar-SA-u-ca-gregory" : "en-US",
    { year: "numeric", month: "short", day: "numeric" });

  const isSuper = user?.role === "super_admin";
  const isBM = user?.role === "branch_manager";
  const isSupervisor = user?.role === "supervisor" || isBM;
  const canSeeAudit = isSuper || user?.role === "area_manager";
  const activeModule = (typeof window !== "undefined" && localStorage.getItem("hct_module")) || "employees";
  const isLicensesModule = activeModule === "licenses";
  const isDayoffModule = activeModule === "dayoff";
  const isEmpMgmtModule = activeModule === "empmgmt";

  const employeesNav = [
    { to: "/", icon: LayoutDashboard, label: t("nav_dashboard"), testid: "nav-dashboard" },
    { to: "/employees", icon: Users, label: t("nav_employees"), testid: "nav-employees" },
    ...(isSupervisor ? [] : [
      { to: "/branches", icon: Building2, label: t("nav_branches"), testid: "nav-branches" },
      { to: "/admins", icon: UserCog, label: t("nav_admins"), testid: "nav-admins" },
    ]),
    { to: "/employees?category=admin", icon: ShieldCheck, label: t("nav_admin_certs"), testid: "nav-admin-certs" },
    ...(canSeeAudit ? [{ to: "/audit-log", icon: History, label: t("nav_audit_log"), testid: "nav-audit-log" }] : []),
    ...(isSuper ? [
      { to: "/area-managers", icon: Crown, label: t("nav_area_managers"), testid: "nav-area-managers" },
      { to: "/positions", icon: Briefcase, label: t("nav_positions"), testid: "nav-positions" },
    ] : []),
  ];
  const licensesNav = [
    { to: "/licenses", icon: Building2, label: "لوحة رخص الفروع", testid: "nav-licenses-dashboard" },
    ...(isSupervisor ? [] : [{ to: "/admins", icon: UserCog, label: t("nav_admins"), testid: "nav-admins-lic" }]),
    ...(isSuper ? [
      { to: "/area-managers", icon: Crown, label: t("nav_area_managers"), testid: "nav-area-managers-lic" },
    ] : []),
    ...(canSeeAudit ? [{ to: "/audit-log", icon: History, label: t("nav_audit_log"), testid: "nav-audit-log-lic" }] : []),
  ];
  const dayoffNav = [
    { to: "/dayoff", icon: CalendarDays, label: t("nav_dayoff"), testid: "nav-dayoff" },
    ...(isSupervisor ? [] : [{ to: "/admins", icon: UserCog, label: t("nav_admins"), testid: "nav-admins-do" }]),
    ...(canSeeAudit ? [{ to: "/audit-log", icon: History, label: t("nav_audit_log"), testid: "nav-audit-log-do" }] : []),
  ];
  const empMgmtNav = [
    { to: "/employee-management", icon: LayoutDashboard, label: t("em_nav_dashboard"), testid: "nav-em-dashboard" },
    { to: "/employee-management/employees", icon: Users, label: t("em_nav_employees"), testid: "nav-em-employees" },
    { to: "/employee-management/transfers", icon: ArrowLeftRight, label: t("em_nav_transfers"), testid: "nav-em-transfers" },
    { to: "/employee-management/promotions", icon: TrendingUp, label: t("em_nav_promotions"), testid: "nav-em-promotions" },
    { to: "/employee-management/vacations", icon: Palmtree, label: t("em_nav_vacations"), testid: "nav-em-vacations" },
    { to: "/employee-management/files", icon: FolderOpen, label: t("em_nav_files"), testid: "nav-em-files" },
    { to: "/employee-management/approvals", icon: ClipboardCheck, label: t("em_nav_approvals"), testid: "nav-em-approvals" },
    { to: "/employee-management/reports", icon: BarChart3, label: t("em_nav_reports"), testid: "nav-em-reports" },
    { to: "/employee-management/audit", icon: History, label: t("em_nav_audit"), testid: "nav-em-audit" },
    { to: "/employee-management/settings", icon: Settings, label: t("em_nav_settings"), testid: "nav-em-settings" },
    ...(isSupervisor ? [] : [{ to: "/admins", icon: UserCog, label: t("nav_admins"), testid: "nav-admins-em" }]),
  ];
  const nav = isEmpMgmtModule ? empMgmtNav : isDayoffModule ? dayoffNav : isLicensesModule ? licensesNav : employeesNav;

  return (
    <div className="App min-h-screen bg-pattern" data-testid="app-layout">
      <header className="brand-gradient text-white shadow-lg sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            {/* Quick back to the module dashboard */}
            {(() => {
              const moduleHome = isEmpMgmtModule ? "/employee-management" : isDayoffModule ? "/dayoff" : isLicensesModule ? "/licenses" : "/";
              if (_p === moduleHome) return null;
              return (
                <button
                  onClick={() => navigate(moduleHome)}
                  className="topnav-link !px-2"
                  title={t("back_to_dashboard")}
                  aria-label={t("back_to_dashboard")}
                  data-testid="header-back-dashboard"
                >
                  <ArrowRight size={18} className={lang === "ar" ? "" : "rotate-180"} />
                </button>
              );
            })()}
            <Brand
              variant="compact"
              title={headerMeta.title}
              subtitle={headerMeta.subtitle}
            />
          </div>

          {/* Today's Hijri date */}
          <div
            className="hidden md:flex items-center gap-2 bg-white/15 rounded-xl px-3 py-1.5 text-white"
            data-testid="today-hijri-badge"
            title={todayG}
          >
            <CalendarDays size={16} />
            <div className="flex flex-col leading-tight">
              <span className="text-[10px] text-teal-100">{t("today_hijri")}</span>
              <span className="text-sm font-semibold">{todayHijriLabel}</span>
            </div>
          </div>

          <nav className="flex items-center gap-1 flex-wrap">
            {/* Only Dashboard stays as a direct top-nav link */}
            <NavLink
              to={isEmpMgmtModule ? "/employee-management" : isLicensesModule ? "/licenses" : "/"}
              end
              className={({ isActive }) => `topnav-link ${isActive ? "active" : ""}`}
              data-testid="nav-dashboard"
            >
              <LayoutDashboard size={16} /> <span className="hidden sm:inline">{isEmpMgmtModule ? t("em_module_name") : isLicensesModule ? "رخص الفروع" : t("nav_dashboard")}</span>
            </NavLink>

            {/* Quick back to the module chooser (sections) */}
            <button
              onClick={() => navigate("/choose")}
              className="topnav-link"
              title={t("back_to_sections")}
              aria-label={t("back_to_sections")}
              data-testid="header-sections-btn"
            >
              <LayoutGrid size={16} /> <span className="hidden sm:inline">{t("sections_label")}</span>
            </button>

            {/* External shortcuts kept in the top bar */}
            <a
              href="https://services.balady.gov.sa/health/inquiry/Certificate-Status?nid=11140"
              target="_blank"
              rel="noopener noreferrer"
              className="topnav-link"
              title={t("shortcut_balady")}
              aria-label={t("shortcut_balady")}
              data-testid="shortcut-balady"
            >
              <Landmark size={16} />
            </a>
            <a
              href="https://maps.app.goo.gl/gTkHaFpd9du2L4AXA"
              target="_blank"
              rel="noopener noreferrer"
              className="topnav-link"
              title={t("shortcut_location")}
              aria-label={t("shortcut_location")}
              data-testid="shortcut-location"
            >
              <MapPin size={16} />
            </a>

            {/* Language toggle */}
            <button onClick={toggleLang} className="topnav-link" data-testid="lang-toggle" title={t("language")}>
              <Globe size={16} /> <span className="hidden sm:inline">{lang === "ar" ? "English" : "عربي"}</span>
            </button>

            {/* Hamburger — opens everything else */}
            <button
              onClick={() => setDrawerOpen(true)}
              className="topnav-link relative"
              title={t("menu")}
              data-testid="drawer-toggle"
            >
              <Menu size={18} />
              {(notifSummary.total || 0) > 0 && (
                <span
                  className="absolute -top-1 start-0 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold leading-none animate-pulse-soft"
                  data-testid="alerts-count-badge"
                >
                  {(notifSummary.total || 0) > 99 ? "99+" : notifSummary.total}
                </span>
              )}
            </button>
          </nav>
        </div>

        {/* Side Drawer */}
        {drawerOpen && (
          <>
            <div
              className="fixed inset-0 bg-black/50 z-40 animate-fade-in"
              onClick={closeDrawer}
              data-testid="drawer-backdrop"
            />
            <aside
              className="fixed top-0 bottom-0 z-50 w-72 max-w-[85vw] bg-white text-slate-900 shadow-2xl flex flex-col animate-slide-in"
              style={{ insetInlineStart: 0 }}
              data-testid="drawer-panel"
            >
              <div className="brand-gradient text-white px-5 py-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {user?.role === "super_admin" ? <Crown size={18} /> : (user?.role === "supervisor" ? <ShieldCheck size={18} /> : <UserCog size={18} />)}
                  <div className="leading-tight">
                    <div className="font-bold text-sm">{user?.full_name || user?.username || "—"}</div>
                    <div className="text-[11px] text-teal-100">
                      {user?.role === "super_admin" ? t("role_super_admin") : user?.role === "supervisor" ? t("role_supervisor") : user?.role === "branch_manager" ? t("role_branch_manager") : t("role_area_manager")}
                    </div>
                  </div>
                </div>
                <button onClick={closeDrawer} className="text-white/80 hover:text-white p-1" data-testid="drawer-close">
                  <X size={18} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto py-3">
                {/* Tracker switcher — top of drawer for easy navigation */}
                <button
                  onClick={() => { closeDrawer(); navigate("/choose"); }}
                  className={`w-full flex items-center gap-3 px-5 py-3 text-sm font-bold border-s-4 transition ${
                    isEmpMgmtModule
                      ? "border-sky-600 bg-sky-50 text-sky-800 hover:bg-sky-100"
                      : isLicensesModule
                      ? "border-fuchsia-600 bg-fuchsia-50 text-fuchsia-800 hover:bg-fuchsia-100"
                      : "border-teal-600 bg-teal-50 text-teal-800 hover:bg-teal-100"
                  }`}
                  data-testid="drawer-switch-tracker"
                >
                  {isEmpMgmtModule ? <Users size={18} /> : isLicensesModule ? <Building2 size={18} /> : <ShieldCheck size={18} />}
                  <span className="flex-1 text-start">
                    {isEmpMgmtModule ? `المتتبع: ${t("em_module_name")}` : isLicensesModule ? "المتتبع: رخص الفروع" : "المتتبع: شهادات الموظفين"}
                  </span>
                  <ChevronsLeftRight size={14} className="opacity-60" />
                </button>
                <div className="text-[10px] text-slate-400 px-5 py-1">اضغط للتبديل بين المتتبعَين</div>
                <div className="border-t border-slate-100 my-2" />

                {nav.slice((isLicensesModule || isEmpMgmtModule) ? 0 : 1).map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.to === "/" || n.to === "/employee-management"}
                    onClick={closeDrawer}
                    className={({ isActive }) => `flex items-center gap-3 px-5 py-3 text-sm font-semibold border-s-4 transition ${isActive ? "border-teal-700 bg-teal-50 text-teal-800" : "border-transparent text-slate-700 hover:bg-slate-50"}`}
                    data-testid={`drawer-${n.testid}`}
                  >
                    <n.icon size={18} />
                    <span>{n.label}</span>
                  </NavLink>
                ))}

                {/* Alerts */}
                <button
                  onClick={() => { closeDrawer(); setAlertsOpen(true); }}
                  className="w-full flex items-center gap-3 px-5 py-3 text-sm font-semibold border-s-4 border-transparent text-slate-700 hover:bg-slate-50"
                  data-testid="drawer-alerts"
                >
                  <Bell size={18} />
                  <span className="flex-1 text-start">{t("alerts")}</span>
                  {(notifSummary.total || 0) > 0 && (
                    <span className="min-w-[22px] px-1.5 py-0.5 rounded-full bg-rose-500 text-white text-[10px] font-bold leading-none" data-testid="drawer-alerts-badge">
                      {notifSummary.total > 99 ? "99+" : notifSummary.total}
                    </span>
                  )}
                </button>

                <div className="border-t border-slate-100 my-2" />

                {user && (
                  <NavLink
                    to="/profile"
                    onClick={closeDrawer}
                    className={({ isActive }) => `flex items-center gap-3 px-5 py-3 text-sm font-semibold border-s-4 transition ${isActive ? "border-teal-700 bg-teal-50 text-teal-800" : "border-transparent text-slate-700 hover:bg-slate-50"}`}
                    data-testid="drawer-profile"
                  >
                    <UserCog size={18} />
                    <span>{t("profile")}</span>
                  </NavLink>
                )}
                {isSuper && (
                  <NavLink
                    to="/system-settings"
                    onClick={closeDrawer}
                    className={({ isActive }) => `flex items-center gap-3 px-5 py-3 text-sm font-semibold border-s-4 transition ${isActive ? "border-slate-700 bg-slate-100 text-slate-800" : "border-transparent text-slate-700 hover:bg-slate-50"}`}
                    data-testid="drawer-system-settings"
                  >
                    <Settings size={18} />
                    <span>{t("nav_system_settings")}</span>
                  </NavLink>
                )}
                <button
                  onClick={() => { closeDrawer(); handleLogout(); }}
                  className="w-full flex items-center gap-3 px-5 py-3 text-sm font-semibold text-rose-600 hover:bg-rose-50"
                  data-testid="drawer-logout"
                >
                  <LogOut size={18} />
                  <span>{t("logout")}</span>
                </button>
              </div>

              <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-xs text-slate-500">
                  <CalendarDays size={13} /> {todayHijriLabel}
                </div>
                <div className="text-[10px] text-slate-400">{todayG}</div>
              </div>
            </aside>
          </>
        )}

        {/* Mobile-only Hijri date row */}
        <div
          className="md:hidden max-w-7xl mx-auto px-4 pb-3 -mt-1 flex items-center gap-2 text-teal-100 text-xs"
          data-testid="today-hijri-mobile"
        >
          <CalendarDays size={12} />
          <span>{t("today_hijri")}: <span className="text-white font-semibold">{todayHijriLabel}</span></span>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        <Outlet />
      </main>

      <NotifSummaryPanel open={alertsOpen} onClose={closeAlerts} summary={notifSummary} />
    </div>
  );
}
